import type {ReactElement} from 'react';
import {useState, useEffect, useRef, useCallback, useMemo} from 'react';
import Slider from '@postermywall/rc-slider';
import 'rc-slider/assets/index.css';
import {Button, ButtonSize, ButtonType} from '@Components/button';
import TooltipTrigger from '@Components/tooltip-trigger';
import {TOOLTIP_POSITION} from '@Components/tooltip';
import {TooltipTriggerEvent} from '@Components/tooltip-trigger/tooltip-trigger';
import {redirectUser} from '@Utils/browser.util';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import styles from './poster-web-page-nav-bar.module.scss';
import QRCode from 'qrcode';
import {useAppSelector} from '@/hooks';
import {getCurrentPageTime, getIsCurrentPagePlaying} from '@Components/poster-editor/poster-editor-reducer';
import {onPosterPause} from '@Components/poster-editor/library/poster-editor-library';
import {isMobile} from 'react-device-detect';
import {Text, TextSize, TextType} from '@Components/text';
import {getPagesArray} from '@Libraries/page-object.library';
import {calculateTotalPlaybackDuration, calculateGlobalPlaybackTime, getPlaybackPositionAtGlobalTime} from './poster-web-page-nav-bar.library';

interface PosterWebPageNavBarProps {
  canEditPoster: boolean;
  qrCodeUrl: string;
  isFullScreen: boolean;
  onToggleFullScreen: () => void;
  isNavBarVisible: boolean;
}

export function PosterWebPageNavBar({canEditPoster, qrCodeUrl, isFullScreen, onToggleFullScreen, isNavBarVisible}: PosterWebPageNavBarProps): ReactElement {
  const pageOrder = useAppSelector((state) => {
    return state.posterEditor.posterObject?.pages.pageOrder ?? [];
  });
  const pagesHashMap = useAppSelector((state) => {
    return state.posterEditor.posterObject?.pages.pagesHashMap;
  });
  const pages = useMemo(() => getPagesArray(pagesHashMap, pageOrder), [pagesHashMap, pageOrder]);
  const currentPageId = useAppSelector((state) => {
    return state.posterEditor.currentPageId;
  });
  const isPlaying = useAppSelector(getIsCurrentPagePlaying);
  const currentTime = useAppSelector(getCurrentPageTime);
  const isVideo = useAppSelector((state) => {
    return state.posterEditor.isVideo;
  });

  const [volume, setVolume] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [dragProgress, setDragProgress] = useState<number | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isDraggingRef = useRef(false);
  const wasPlayingBeforeSeekRef = useRef(false);
  const isMuted = volume === 0;

  const getCurrentPageNumber = (): number => {
    if (!currentPageId || pageOrder.length === 0) {
      return 1;
    }
    return pageOrder.indexOf(currentPageId) + 1;
  };

  const goToPage = (targetIndex: number, shouldAutoPlay?: boolean): void => {
    if (targetIndex < 0 || targetIndex >= pageOrder.length) {
      return;
    }

    const targetPageId = pageOrder[targetIndex];
    const wasPlaying = isPlaying;

    void window.posterEditor?.whiteboard?.pause().then(() => {
      window.posterEditor?.whiteboard?.setCurrentPage(targetPageId);
      void window.posterEditor?.whiteboard?.pages.getPage(targetPageId)?.seek(0).then(() => {
        if (shouldAutoPlay && wasPlaying) {
          void window.posterEditor?.whiteboard?.play(targetPageId);
        }
      });
    });
  };

  const onPrevPage = (): void => {
    if (isSeeking || pageOrder.length === 0 || !currentPageId) {
      return;
    }

    const currentPageIndex = pageOrder.indexOf(currentPageId);
    const prevIndex = currentPageIndex > 0 ? currentPageIndex - 1 : 0;
    goToPage(prevIndex, isPlaying);
  };

  const onNextPage = (): void => {
    if (isSeeking || pageOrder.length === 0 || !currentPageId) {
      return;
    }

    const currentPageIndex = pageOrder.indexOf(currentPageId);
    const nextIndex = currentPageIndex < pageOrder.length - 1 ? currentPageIndex + 1 : 0;
    goToPage(nextIndex, isPlaying);
  };

  const onPlayPauseClick = (): void => {
    if (isSeeking || !currentPageId) {
      return;
    }

    const poster = window.posterEditor?.whiteboard;
    if (!poster?.isInitialized) {
      return;
    }

    if (isPlaying) {
      void poster.pause();
    } else {
      const totalDuration = calculateTotalPlaybackDuration(pages);
      const globalTime = calculateGlobalPlaybackTime(pages, currentPageId, currentTime);
      const isAtEnd = totalDuration > 0 && globalTime >= totalDuration - 0.05;

      if (isAtEnd) {
        const firstPageId = pageOrder[0];
        if (firstPageId) {
          poster.setCurrentPage(firstPageId);
          void poster.pages.getPage(firstPageId)?.seek(0).then(() => {
            void poster.play(firstPageId);
          });
        }
      } else {
        void poster.play(currentPageId);
      }
    }
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent): void => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      switch (e.key) {
        case ' ':
          e.preventDefault();
          onPlayPauseClick();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          onPrevPage();
          break;
        case 'ArrowRight':
          e.preventDefault();
          onNextPage();
          break;
        case 'ArrowUp':
          e.preventDefault();
          adjustVolume(5);
          break;
        case 'ArrowDown':
          e.preventDefault();
          adjustVolume(-5);
          break;
        default:
          if (e.key.toLowerCase() === 'm') {
            e.preventDefault();
            onVolumeClick();
          }
          break;
      }
    },
    [currentPageId, isPlaying, pageOrder, isSeeking, volume]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return (): void => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    const poster = window.posterEditor?.whiteboard;
    if (poster && currentPageId && volume === 0) {
      window.posterEditor?.whiteboard?.setPosterVolume(0);
    }
  }, [currentPageId]);

  useEffect(() => {
    if (!isNavBarVisible) {
      setIsMenuOpen(false);
    }
  }, [isNavBarVisible]);

  useEffect(() => {
    if (!isMobile || !isMenuOpen) {
      return;
    }

    const closeMenu = (): void => {
      setIsMenuOpen(false);
    };
    const poster = window.posterEditor?.whiteboard;

    if (!poster) {
      return;
    }

    for (const page of Object.values(poster.pages.pagesHashMap)) {
      page.fabricCanvas?.on('mouse:down', closeMenu);
    }

    return (): void => {
      for (const page of Object.values(poster.pages.pagesHashMap)) {
        page.fabricCanvas?.off('mouse:down', closeMenu);
      }
    };
  }, [isMobile, isMenuOpen]);

  const onVolumeClick = (): void => {
    setVolumeValue(isMuted ? 100 : 0);
  };

  const setVolumeValue = (newVolume: number): void => {
    const clampedVolume = Math.max(0, Math.min(100, newVolume));
    setVolume(clampedVolume);
    window.posterEditor?.whiteboard?.setPosterVolume(clampedVolume / 100);
  };

  const onVolumeChange = (value: number | number[]): void => {
    const newVolume = Array.isArray(value) ? value[0] : value;
    setVolumeValue(newVolume);
  };

  const adjustVolume = (delta: number): void => {
    setVolumeValue(volume + delta);
  };

  const getPlayPauseIcon = (): string => {
    return isPlaying ? 'icon-pause' : 'icon-play-video';
  };

  const getVolumeIcon = (): string => {
    return isMuted ? 'icon-sound-off' : 'icon-sound';
  };

  const getTimelineProgress = (): number => {
    const totalDuration = calculateTotalPlaybackDuration(pages);
    if (totalDuration === 0) {
      return 0;
    }
    if (dragProgress !== null) {
      return dragProgress;
    }
    const globalTime = calculateGlobalPlaybackTime(pages, currentPageId, currentTime);
    return Math.min(100, Math.max(0, (globalTime / totalDuration) * 100));
  };

  const onSliderChange = (value: number | number[]): void => {
    const numValue = Array.isArray(value) ? value[0] : value;
    setDragProgress(numValue);
    setIsSeeking(true);

    if (!isDraggingRef.current) {
      isDraggingRef.current = true;
      wasPlayingBeforeSeekRef.current = isPlaying;
      onPosterPause();
    }
  };

  const onSliderAfterChange = (value: number | number[]): void => {
    const numValue = Array.isArray(value) ? value[0] : value;
    setIsSeeking(false);
    setDragProgress(null);

    const totalDuration = calculateTotalPlaybackDuration(pages);
    if (totalDuration === 0) {
      isDraggingRef.current = false;
      return;
    }

    const targetGlobalTime = (numValue / 100) * totalDuration;
    const poster = window.posterEditor?.whiteboard;
    if (!poster) {
      isDraggingRef.current = false;
      return;
    }

    const target = getPlaybackPositionAtGlobalTime(pages, targetGlobalTime);
    if (!target) {
      isDraggingRef.current = false;
      return;
    }

    if (target.pageId !== currentPageId) {
      poster.setCurrentPage(target.pageId);
    }
    void poster.pages.getPage(target.pageId)?.seek(target.localTime);
  };

  useEffect(() => {
    const handlePointerUp = (): void => {
      if (!isDraggingRef.current) {
        return;
      }

      isDraggingRef.current = false;

      if (wasPlayingBeforeSeekRef.current) {
        const poster = window.posterEditor?.whiteboard;
        if (poster) {
          void poster.play(poster.getCurrentPageId());
        }
      }
    };

    window.addEventListener('pointerup', handlePointerUp);
    return (): void => {
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, []);

  const onCopyShareableLink = async (): Promise<void> => {
    const link = window.posterEditor?.whiteboard?.getWebpageLink();
    if (link) {
      await window.PMW.copyToClipboard(link);
    }
  };

  const onEdit = (): void => {
    const link = window.posterEditor?.whiteboard?.getEditLink();
    if (link) {
      redirectUser(link, false, true);
    }
  };

  const onDownloadQrCode = async (): Promise<void> => {
    const qrDataUrl = await QRCode.toDataURL(qrCodeUrl, {
      width: 130,
      margin: 0,
      errorCorrectionLevel: 'M',
    });
    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = 'QR-Code.png';
    link.click();
    link.remove();
  };

  const hasMultiplePages = pageOrder.length > 1;
  const isPlayable = hasMultiplePages || isVideo;
  const fullscreenIcon = isFullScreen ? 'icon-exit-fullscreen' : 'icon-fit-to-screen';
  const fullscreenTooltip = isFullScreen ? window.i18next.t('pmwjs_exit_fullscreen') : window.i18next.t('pmwjs_fullscreen');
  const actionButtonClass = `${styles.rightActionButton} spacing-m-r-2`;

  const renderTimelineSlider = (): ReactElement | null => {
    if (!isPlayable) {
      return null;
    }

    return (
      <div className={`${styles.topSliderBar} _full-width _position-absolute _cursor-pointer _overflow-visible spacing-p-0`}>
        <Slider
          min={0}
          max={100}
          value={getTimelineProgress()}
          onChange={onSliderChange}
          onAfterChange={onSliderAfterChange}
          className={styles.webpageSlider}
          disabled={calculateTotalPlaybackDuration(pages) === 0}
          step={0.1}
        />
      </div>
    );
  };

  const renderPlaybackControls = (): ReactElement | null => {
    if (!isPlayable) {
      return null;
    }

    return (
      <>
        <button
          className={`${styles.controlButton} flex-center bg-transparent _cursor-pointer spacing-p-2 spacing-m-r-2`}
          onClick={onPlayPauseClick}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          <Icon icon={getPlayPauseIcon()} size={IconSize.SIZE_ICON_20} type={IconType.DARK_GHOST} />
        </button>
        <div className={`${styles.volumeControl} flexbox flex-items-center gap-2`}>
          <button
            className={`${styles.controlButton} flex-center bg-transparent _cursor-pointer spacing-p-2 spacing-m-r-2`}
            onClick={onVolumeClick}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            <Icon icon={getVolumeIcon()} size={IconSize.SIZE_ICON_20} type={IconType.DARK_GHOST} />
          </button>
          {!isMobile ? <Slider min={0} max={100} value={volume} onChange={onVolumeChange} className={styles.volumeSlider} step={1} /> : null}
        </div>
      </>
    );
  };

  const renderPageIndicator = (): ReactElement => (
    <Text val={`${getCurrentPageNumber()} / ${pageOrder.length ? pageOrder.length : 1}`} size={TextSize.XXSMALL} type={TextType.BODY} />
  );

  const renderCenterNavigation = (): ReactElement | null => {
    if (!hasMultiplePages) {
      return null;
    }

    return (
      <>
        <button className={`${styles.navCaretButton} flex-center bg-transparent _cursor-pointer`} onClick={onPrevPage} aria-label="Previous page">
          <Icon icon="icon-caret-left" size={IconSize.SIZE_ICON_20} />
        </button>
        {renderPageIndicator()}
        <button className={`${styles.navCaretButton} flex-center bg-transparent _cursor-pointer`} onClick={onNextPage} aria-label="Next page">
          <Icon icon="icon-caret-right" size={IconSize.SIZE_ICON_20} />
        </button>
      </>
    );
  };

  const renderMobileMenuContent = (): ReactElement => (
    <div className="flexbox flex-column">
      {canEditPoster ? (
        <button
          className={`${styles.mobileMenuItem} flexbox flex-items-center gap-3 _cursor-pointer _full-width spacing-p-t-3 spacing-p-b-3 spacing-p-l-4 spacing-p-r-4`}
          onClick={(): void => void onDownloadQrCode()}
        >
          <Icon icon="icon-qr-content" size={IconSize.SIZE_ICON_20} type={IconType.DARK_GHOST} />
          <span>{window.i18next.t('pmwjs_download_qr_code')}</span>
        </button>
      ) : null}
      {canEditPoster ? (
        <button
          className={`${styles.mobileMenuItem} flexbox flex-items-center gap-3 _cursor-pointer _full-width spacing-p-t-3 spacing-p-b-3 spacing-p-l-4 spacing-p-r-4`}
          onClick={onEdit}
        >
          <Icon icon="icon-pencil" size={IconSize.SIZE_ICON_20} type={IconType.DARK_GHOST} />
          <span>{window.i18next.t('pmwjs_edit')}</span>
        </button>
      ) : null}
      <button
        className={`${styles.mobileMenuItem} flexbox flex-items-center gap-3 _cursor-pointer _full-width spacing-p-t-3 spacing-p-b-3 spacing-p-l-4 spacing-p-r-4`}
        onClick={(): void => void onCopyShareableLink()}
      >
        <Icon icon="icon-link" size={IconSize.SIZE_ICON_20} type={IconType.DARK_GHOST} />
        <span>{window.i18next.t('pmwjs_copy_shareable_link')}</span>
      </button>
    </div>
  );

  const renderMobileActions = (): ReactElement => (
    <>
      <Dropdown
        selector={
          <button className={`${styles.mobileMenuButton} flex-center bg-transparent _cursor-pointer spacing-p-2`} aria-label="More options">
            <Icon icon="icon-menu-dots" size={IconSize.SIZE_ICON_20} type={IconType.DARK_GHOST} shape={IconShape.SQUARE} />
          </button>
        }
        popup={renderMobileMenuContent()}
        position={DROPDOWN_POSITION.FIXED}
        alignment={ALIGNMENT_TYPE.BOTTOM_END}
        popupClassName={styles.mobileMenuPopup}
        isControlled={true}
        open={isMenuOpen}
        onOpen={(): void => {
          setIsMenuOpen(true);
        }}
        onClose={(): void => {
          setIsMenuOpen(false);
        }}
      />
      <Button onClick={onToggleFullScreen} customClasses={`${styles.rightActionButton} spacing-m-l-2`} size={ButtonSize.LARGE} type={ButtonType.TERTIARY} onDark text="" icon={fullscreenIcon} />
    </>
  );

  const renderDesktopActions = (): ReactElement => (
    <>
      {canEditPoster && (
        <TooltipTrigger text={window.i18next.t('pmwjs_download_qr_code')} position={TOOLTIP_POSITION.BOTTOM_MIDDLE} tooltipClass={styles.tooltipTop}>
          <Button onClick={(): void => void onDownloadQrCode()} customClasses={actionButtonClass} size={ButtonSize.LARGE} type={ButtonType.TERTIARY} onDark text="" icon="icon-qr-content" />
        </TooltipTrigger>
      )}
      {canEditPoster && (
        <TooltipTrigger text={window.i18next.t('pmwjs_edit')} position={TOOLTIP_POSITION.BOTTOM_MIDDLE} tooltipClass={styles.tooltipTop}>
          <Button onClick={onEdit} customClasses={actionButtonClass} size={ButtonSize.LARGE} type={ButtonType.TERTIARY} onDark text="" icon="icon-pencil" />
        </TooltipTrigger>
      )}
      <TooltipTrigger text={window.i18next.t('pmwjs_copy_shareable_link')} position={TOOLTIP_POSITION.BOTTOM_MIDDLE} tooltipClass={styles.tooltipTop}>
        <TooltipTrigger
          text={window.i18next.t('pmwjs_link_copied')}
          position={TOOLTIP_POSITION.BOTTOM_MIDDLE}
          triggerEvent={TooltipTriggerEvent.CLICK}
          tooltipClass={styles.tooltipTop}
        >
          <Button onClick={(): void => void onCopyShareableLink()} customClasses={actionButtonClass} size={ButtonSize.LARGE} type={ButtonType.TERTIARY} onDark text="" icon="icon-link" />
        </TooltipTrigger>
      </TooltipTrigger>
      <TooltipTrigger text={fullscreenTooltip} position={TOOLTIP_POSITION.BOTTOM_MIDDLE} tooltipClass={styles.tooltipTop}>
        <Button onClick={onToggleFullScreen} customClasses={actionButtonClass} size={ButtonSize.LARGE} type={ButtonType.TERTIARY} onDark text="" icon={fullscreenIcon} />
      </TooltipTrigger>
    </>
  );

  return (
    <div
      id="nav"
      className={`${styles.nav} ${isNavBarVisible ? styles.navIn : ''} flexbox _full-width _position-absolute flex-justify-between flex-items-center spacing-p-t-5 spacing-p-b-4 spacing-p-l-6 spacing-p-r-6`}
    >
      {renderTimelineSlider()}
      <div className="flexbox flex-items-center gap-2">{renderPlaybackControls()}</div>
      <div className={`${styles.centerNavigation} flexbox flex-items-center gap-2 _position-absolute`}>{renderCenterNavigation()}</div>
      <div className="flexbox">{isMobile ? renderMobileActions() : renderDesktopActions()}</div>
    </div>
  );
}
